import React, { useEffect, useState, useRef } from "react";
import { api, apiError, API } from "@/lib/apiClient";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatDate, formatDateTime, initials } from "@/lib/statuts";
import { useStatuts } from "@/context/StatutsContext";
import { StatutBadge } from "@/components/StatutBadge";
import {
  ArrowLeft, User, MapPin, Phone, Mail, Calendar, Clock, Wrench, Building2, FileText,
  Upload, Loader2, History, MessageSquare, Save, Trash2, Download, ChevronDown, Pencil,
} from "lucide-react";
import { toast } from "sonner";
import VisiteFormModal from "@/components/VisiteFormModal";
import { TimeInput } from "@/components/TimeInput";

export default function VisiteDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const { statuts } = useStatuts();
  const navigate = useNavigate();
  const [v, setV] = useState(null);
  const [error, setError] = useState("");
  const [techs, setTechs] = useState([]);
  const [comment, setComment] = useState("");
  const [uploading, setUploading] = useState(false);
  const [statutOpen, setStatutOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const fileRef = useRef();
  const canEdit = ["admin", "planification"].includes(user.role);
  const canStatut = ["admin", "planification", "technicien"].includes(user.role);

  const load = async () => {
    try {
      const { data } = await api.get(`/visites/${id}`);
      setV(data);
    } catch (e) {
      setError(apiError(e));
    }
  };

  useEffect(() => { load(); }, [id]);
  useEffect(() => { if (canEdit) api.get("/users/techniciens").then((r) => setTechs(r.data)).catch(() => {}); }, [canEdit]);

  const changeStatut = async (code) => {
    setStatutOpen(false);
    try { await api.patch(`/visites/${id}/statut`, { statut: code }); toast.success("Statut mis à jour"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const savePlanning = async (patch) => {
    try { await api.patch(`/visites/${id}/planning`, patch); toast.success("Planning mis à jour"); load(); }
    catch (e) {
      if (e?.response?.status === 409) {
        if (window.confirm(apiError(e) + "\n\nForcer quand même ?")) {
          try { await api.patch(`/visites/${id}/planning`, { ...patch, force: true }); toast.success("Forcé"); load(); }
          catch (e2) { toast.error(apiError(e2)); }
        }
      } else toast.error(apiError(e));
    }
  };

  const addComment = async () => {
    const txt = comment.trim();
    if (!txt) return;
    try { await api.post(`/visites/${id}/commentaires`, { texte: txt }); setComment(""); toast.success("Commentaire ajouté"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/visites/${id}/documents?type_doc=Document`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Document ajouté");
      load();
    } catch (err) { toast.error(apiError(err)); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const download = async (docId) => {
    try {
      const { data } = await api.post(`/documents/${docId}/download-token`);
      window.open(`${API}/documents/${docId}/download?token=${encodeURIComponent(data.token)}`, "_blank");
    } catch (err) { toast.error(apiError(err)); }
  };

  if (error) return <div className="rounded-xl border border-rose-200 bg-rose-50 p-6 text-rose-700">{error}</div>;
  if (!v) return <div className="flex h-96 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;

  return (
    <div className="space-y-4">
      <button onClick={() => navigate(-1)} className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-900"><ArrowLeft className="h-4 w-4" /> Retour</button>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <span className="mono rounded-lg bg-amber-100 px-2.5 py-1 text-sm font-bold text-amber-700">{v.numero_dossier}</span>
          <h1 className="font-heading text-2xl font-extrabold tracking-tight text-slate-900">{v.client_nom}</h1>
        </div>
        <div className="flex items-center gap-2">
          {canEdit && (
            <button data-testid="edit-visite-btn" onClick={() => setEditOpen(true)} className="flex items-center gap-1.5 rounded-lg bg-[#0A0A0A] px-3 py-2 text-sm font-semibold text-white hover:bg-slate-800">
              <Pencil className="h-4 w-4" /> Modifier
            </button>
          )}
          <div className="relative">
          {canStatut ? (
            <>
              <button data-testid="statut-dropdown" onClick={() => setStatutOpen((s) => !s)} className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2">
                <StatutBadge code={v.statut} color={v.statut_color} label={v.statut_label} /><ChevronDown className="h-4 w-4 text-slate-400" />
              </button>
              {statutOpen && (
                <div className="absolute right-0 z-20 mt-1 w-56 rounded-lg border border-slate-200 bg-white py-1 shadow-lg">
                  {statuts.map((s) => (
                    <button key={s.code} data-testid={`set-statut-${s.code}`} onClick={() => changeStatut(s.code)} className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-slate-50">
                      <span className="h-2.5 w-2.5 rounded-full" style={{ background: s.color }} /> {s.label}
                    </button>
                  ))}
                </div>
              )}
            </>
          ) : <StatutBadge code={v.statut} color={v.statut_color} label={v.statut_label} />}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-12">
        <div className="space-y-4 lg:col-span-8">
          <Section title="Informations client" icon={User}>
            <Grid>
              <Info label="Nom complet" value={v.client_nom} />
              <Info icon={Phone} label="Téléphone" value={v.client_telephone} mono />
              <Info icon={Mail} label="Email" value={v.client?.email || "—"} />
              <Info icon={MapPin} label="Adresse" value={`${v.adresse || v.client?.adresse || ""} ${v.complement_adresse || ""}`} />
              <Info label="Code postal" value={v.code_postal} mono />
              <Info label="Ville" value={v.ville} />
            </Grid>
          </Section>

          <Section title="Informations rendez-vous" icon={Calendar}>
            <Grid>
              <EditableDate canEdit={canEdit} label="Date de visite" value={v.date_visite} onSave={(val) => savePlanning({ date_visite: val })} />
              <EditableTime canEdit={canEdit} label="Horaire" hd={v.heure_debut} hf={v.heure_fin} onSave={(hd, hf) => savePlanning({ heure_debut: hd, heure_fin: hf })} />
              <EditableTech canEdit={canEdit} label="Technicien" value={v.technicien_id} display={v.technicien_nom} techs={techs} onSave={(val) => savePlanning({ technicien_id: val })} />
              {user.role !== "technicien" && <Info icon={Building2} label="Partenaire" value={v.installateur_nom} />}
              <Info icon={Wrench} label="Type de visite" value={v.type_visite} />
              {user.role !== "technicien" && <Info label="Unité d'affaire" value={v.unite_affaire} />}
              {user.role === "admin" && <Info label="Prix de la visite" value={v.prix != null ? `${v.prix.toLocaleString("fr-FR")} €` : "—"} />}
            </Grid>
            {v.instructions && <div className="mt-3 rounded-lg bg-amber-50 p-3 text-sm text-amber-800"><b>Instructions :</b> {v.instructions}</div>}
          </Section>

          <Section title="Documents" icon={FileText} action={
            <label className="flex cursor-pointer items-center gap-1.5 rounded-lg bg-[#0A0A0A] px-3 py-1.5 text-xs font-semibold text-white hover:bg-slate-800">
              {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />} Ajouter
              <input ref={fileRef} data-testid="doc-upload" type="file" className="hidden" onChange={upload} />
            </label>
          }>
            {(v.documents_demandes && v.documents_demandes.length > 0) && (
              <div className="mb-4 rounded-lg bg-amber-50 p-3">
                <div className="mb-2 text-[11px] font-bold uppercase tracking-wide text-amber-700">Documents demandés</div>
                <div className="space-y-1">
                  {v.documents_demandes.map((d) => {
                    const fourni = (v.documents || []).some((doc) => (doc.nom || "").toLowerCase().includes(d.split(" ")[0].toLowerCase()));
                    return (
                      <div key={d} className="flex items-center justify-between text-sm">
                        <span className="text-slate-700">{d}</span>
                        <span className={`text-xs font-semibold ${fourni ? "text-emerald-600" : "text-amber-600"}`}>{fourni ? "Fourni" : "En attente"}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            {v.documents.length === 0 ? <p className="text-sm text-slate-400">Aucun document fourni.</p> : (
              <div className="divide-y divide-slate-100">
                {v.documents.map((d) => (
                  <div key={d.id} className="flex items-center justify-between py-2.5">
                    <div className="flex items-center gap-3">
                      <FileText className="h-5 w-5 text-slate-400" />
                      <div>
                        <div className="text-sm font-medium text-slate-800">{d.nom}</div>
                        <div className="text-xs text-slate-400">{d.type} · {(d.taille / 1024).toFixed(0)} Ko · {formatDate(d.date)} · {d.uploaded_by_nom}</div>
                      </div>
                    </div>
                    <button data-testid={`doc-download-${d.id}`} onClick={() => download(d.id)} className="rounded-lg p-2 text-slate-500 hover:bg-slate-100"><Download className="h-4 w-4" /></button>
                  </div>
                ))}
              </div>
            )}
          </Section>

          <Section title="Commentaires" icon={MessageSquare}>
            {(!v.commentaires_list || v.commentaires_list.length === 0) ? (
              <p className="text-sm text-slate-400">Aucun commentaire pour le moment.</p>
            ) : (
              <div className="space-y-3">
                {v.commentaires_list.map((cm) => (
                  <div key={cm.id} data-testid="comment-item" className="rounded-lg bg-slate-50 p-3">
                    <div className="flex items-center gap-1.5 text-[11px] text-slate-400"><MessageSquare className="h-3 w-3" /> {cm.auteur_nom} · {formatDateTime(cm.date)}</div>
                    <div className="mt-0.5 whitespace-pre-wrap text-sm text-slate-800">{cm.texte}</div>
                  </div>
                ))}
              </div>
            )}
            {canEdit && (
              <div className="mt-3 flex items-start gap-2">
                <textarea data-testid="comment-input" value={comment} onChange={(e) => setComment(e.target.value)} rows={2} className="flex-1 rounded-lg border border-slate-200 p-2.5 text-sm outline-none focus:border-slate-400" placeholder="Ajouter un commentaire…" />
                <button data-testid="comment-save" onClick={addComment} disabled={!comment.trim()} className="flex items-center gap-1.5 rounded-lg bg-slate-900 px-3 py-2 text-xs font-semibold text-white hover:bg-slate-700 disabled:opacity-50"><Save className="h-3.5 w-3.5" /> Ajouter</button>
              </div>
            )}
          </Section>
        </div>

        {user.role !== "technicien" && (
          <div className="lg:col-span-4">
            <Section title="Historique" icon={History}>
              <div className="space-y-4">
                {v.historique.map((h) => (
                  <div key={h.id} className="relative pl-5">
                    <span className="absolute left-0 top-1.5 h-2.5 w-2.5 rounded-full bg-blue-500" />
                    <span className="absolute left-[4.5px] top-4 h-full w-px bg-slate-200" />
                    <div className="text-xs font-semibold text-slate-800">{h.description}</div>
                    {h.ancienne_valeur && <div className="text-xs text-slate-400">{h.ancienne_valeur} → <b className="text-slate-600">{h.nouvelle_valeur}</b></div>}
                    {!h.ancienne_valeur && h.nouvelle_valeur && <div className="text-xs text-slate-500">{h.nouvelle_valeur}</div>}
                    <div className="mt-0.5 text-[11px] text-slate-400">{formatDateTime(h.date)} · {h.user_name}</div>
                  </div>
                ))}
              </div>
            </Section>
          </div>
        )}
      </div>
      {editOpen && <VisiteFormModal mode="edit" visite={v} onClose={() => setEditOpen(false)} onSaved={() => { setEditOpen(false); load(); }} />}
    </div>
  );
}

function Section({ title, icon: Icon, action, children }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5">
      <div className="mb-4 flex items-center justify-between">
        <div className="flex items-center gap-2">
          {Icon && <Icon className="h-4 w-4 text-blue-600" />}
          <h3 className="font-heading text-sm font-bold uppercase tracking-wide text-slate-700">{title}</h3>
        </div>
        {action}
      </div>
      {children}
    </div>
  );
}
const Grid = ({ children }) => <div className="grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-2">{children}</div>;
function Info({ icon: Icon, label, value, mono }) {
  return (
    <div>
      <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">{label}</div>
      <div className={`flex items-center gap-1.5 text-sm text-slate-800 ${mono ? "mono" : ""}`}>{Icon && <Icon className="h-3.5 w-3.5 text-slate-400" />}{value || "—"}</div>
    </div>
  );
}
function EditableDate({ canEdit, label, value, onSave }) {
  const [edit, setEdit] = useState(false);
  const [val, setVal] = useState(value || "");
  if (!canEdit || !edit) return (
    <div onClick={() => canEdit && setEdit(true)} className={canEdit ? "cursor-pointer" : ""}>
      <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">{label}</div>
      <div className="flex items-center gap-1.5 text-sm text-slate-800"><Calendar className="h-3.5 w-3.5 text-slate-400" />{value ? formatDate(value) : "—"}</div>
    </div>
  );
  return (
    <div>
      <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">{label}</div>
      <div className="flex gap-1">
        <input type="date" value={val} onChange={(e) => setVal(e.target.value)} className="rounded border border-slate-200 px-1.5 py-1 text-sm" />
        <button onClick={() => { onSave(val); setEdit(false); }} className="rounded bg-blue-600 px-2 text-xs text-white">OK</button>
      </div>
    </div>
  );
}
function EditableTime({ canEdit, label, hd, hf, onSave }) {
  const [edit, setEdit] = useState(false);
  const [a, setA] = useState(hd || ""); const [b, setB] = useState(hf || "");
  if (!canEdit || !edit) return (
    <div onClick={() => canEdit && setEdit(true)} className={canEdit ? "cursor-pointer" : ""}>
      <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">{label}</div>
      <div className="flex items-center gap-1.5 text-sm text-slate-800"><Clock className="h-3.5 w-3.5 text-slate-400" />{hd ? `${hd} – ${hf || ""}` : "—"}</div>
    </div>
  );
  return (
    <div>
      <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">{label}</div>
      <div className="flex gap-1">
        <TimeInput value={a} onChange={(v) => setA(v)} className="w-16 rounded border border-slate-200 px-1 py-1 text-xs" />
        <TimeInput value={b} onChange={(v) => setB(v)} className="w-16 rounded border border-slate-200 px-1 py-1 text-xs" />
        <button onClick={() => { onSave(a, b); setEdit(false); }} className="rounded bg-blue-600 px-2 text-xs text-white">OK</button>
      </div>
    </div>
  );
}
function EditableTech({ canEdit, label, value, display, techs, onSave }) {
  const [edit, setEdit] = useState(false);
  if (!canEdit || !edit) return (
    <div onClick={() => canEdit && setEdit(true)} className={canEdit ? "cursor-pointer" : ""}>
      <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">{label}</div>
      <div className="flex items-center gap-1.5 text-sm text-slate-800"><Wrench className="h-3.5 w-3.5 text-slate-400" />{display || "Non affecté"}</div>
    </div>
  );
  return (
    <div>
      <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">{label}</div>
      <select data-testid="edit-technicien" defaultValue={value || ""} onChange={(e) => { onSave(e.target.value); setEdit(false); }} className="rounded border border-slate-200 px-1.5 py-1 text-sm">
        <option value="">Non affecté</option>
        {techs.map((t) => <option key={t.id} value={t.id}>{t.prenom} {t.nom}</option>)}
      </select>
    </div>
  );
}
